import React, { useMemo, useState } from "react";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const MortgageCalculator = () => {
  const [price, setPrice] = useState(2000000);
  const [down, setDown] = useState(20);
  const [rate, setRate] = useState(6.25);
  const [years, setYears] = useState(30);

  const { monthly, principal, totalInterest } = useMemo(() => {
    const loan = price * (1 - down / 100);
    const r = rate / 100 / 12;
    const n = years * 12;
    const m = r === 0 ? loan / n : (loan * r) / (1 - Math.pow(1 + r, -n));
    return { monthly: m || 0, principal: loan, totalInterest: m * n - loan };
  }, [price, down, rate, years]);

  return (
    <div data-testid="mortgage-calculator">
      <section className="bg-navy text-white py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="overline text-gold">Tools</div>
          <h1 className="font-display text-4xl lg:text-6xl mt-4">Mortgage Calculator</h1>
          <p className="mt-4 max-w-xl text-white/70">Estimate your monthly payment across price, down payment, rate, and term.</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-2 gap-10">
        <div className="space-y-6">
          <div>
            <div className="flex justify-between text-sm text-slate-600 mb-2"><span>Home Price</span><span className="font-semibold text-navy">{fmt(price)}</span></div>
            <input type="range" min="200000" max="25000000" step="50000" value={price} onChange={(e) => setPrice(Number(e.target.value))} className="w-full accent-gold-dark" data-testid="mc-price" />
          </div>
          <div>
            <div className="flex justify-between text-sm text-slate-600 mb-2"><span>Down Payment</span><span className="font-semibold text-navy">{down}% ({fmt(price * down / 100)})</span></div>
            <input type="range" min="0" max="80" step="1" value={down} onChange={(e) => setDown(Number(e.target.value))} className="w-full accent-gold-dark" data-testid="mc-down" />
          </div>
          <div>
            <div className="flex justify-between text-sm text-slate-600 mb-2"><span>Interest Rate</span><span className="font-semibold text-navy">{rate.toFixed(2)}%</span></div>
            <input type="range" min="2" max="12" step="0.05" value={rate} onChange={(e) => setRate(Number(e.target.value))} className="w-full accent-gold-dark" data-testid="mc-rate" />
          </div>
          <div>
            <div className="flex justify-between text-sm text-slate-600 mb-2"><span>Term</span><span className="font-semibold text-navy">{years} years</span></div>
            <input type="range" min="5" max="40" step="5" value={years} onChange={(e) => setYears(Number(e.target.value))} className="w-full accent-gold-dark" data-testid="mc-years" />
          </div>
        </div>

        <div className="bg-navy text-white p-10">
          <div className="overline text-gold">Estimated Monthly Payment</div>
          <div className="font-display text-5xl lg:text-6xl text-gold mt-4" data-testid="mc-monthly">{fmt(monthly)}</div>
          <div className="mt-8 space-y-3 text-sm">
            <div className="flex justify-between border-b border-white/10 pb-3"><span className="text-white/70">Loan Principal</span><span className="font-semibold">{fmt(principal)}</span></div>
            <div className="flex justify-between border-b border-white/10 pb-3"><span className="text-white/70">Total Interest</span><span className="font-semibold">{fmt(totalInterest)}</span></div>
            <div className="flex justify-between border-b border-white/10 pb-3"><span className="text-white/70">Total Paid</span><span className="font-semibold">{fmt(principal + totalInterest)}</span></div>
          </div>
          <p className="text-xs text-white/50 mt-8 leading-relaxed">Estimates only. Excludes taxes, insurance, HOA. Consult your mortgage advisor.</p>
        </div>
      </section>
    </div>
  );
};

export default MortgageCalculator;
